import { AUFTRAG_API } from '@Constants/backendConstants';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { type ApiOrder } from '@Model';
import {
    EditFrameMode,
    FrameFallback,
    type IFrameEditShellProps,
    useEditFrame,
    useFrameEditBaseNavigation,
    useOnTheFlyModal,
} from '@synqo/frontend-component-library';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { OrderFrameBody } from './Order/OrderFrameBody';

export interface FrameEditOrderProps extends IFrameEditShellProps<ApiOrder> {
    staging?: boolean;
}

// @ai-generated 2026-10-22T10:53:17+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function FrameEditOrder(props: FrameEditOrderProps) {
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    return (
        <OrderEditFrame
            key={primaryKey}
            {...props}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
        />
    );
}

// @ai-generated 2026-10-26T09:22:54+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
function useOpenOrder() {
    const { openOnTheFlyModal, closeOnTheFlyModals } = useOnTheFlyModal();

    return useCallback(
        (orderId: string) => {
            let modalId = '';
            modalId = openOnTheFlyModal({
                child: (
                    <FrameEditOrder
                        primaryKey={orderId}
                        events={{ onClose: () => closeOnTheFlyModals(modalId), onBack: undefined, onAdd: undefined }}
                    />
                ),
            });
        },
        [openOnTheFlyModal, closeOnTheFlyModals],
    );
}

function OrderEditFrame(props: FrameEditOrderProps & { primaryKey: string }) {
    const hasPermission = useHasPermission();
    const openOrder = useOpenOrder();
    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    const events = useMemo(
        () => ({
            ...frameEditBaseNavigationEvents,
            ...props.events,
        }),
        [frameEditBaseNavigationEvents, props.events],
    );

    const frame = useEditFrame<ApiOrder>({
        apiResourcePath: `${AUFTRAG_API}/orders`,
        primaryKey: props.primaryKey,
        include: ['positions', 'container_actions'],
        embedded: props.embedded,
        events,
        canEdit: props.canEdit !== false && hasPermission(Permission.AuftragsbearbeitungOrdersUpdate),
        canCreate: props.canCreate !== false && hasPermission(Permission.AuftragsbearbeitungOrdersCreate),
        canDelete: props.canDelete !== false && hasPermission(Permission.AuftragsbearbeitungOrdersDelete),
        initialMode: props.staging ? EditFrameMode.View : undefined,
    });

    if (frame.status !== 'ready') {
        return <FrameFallback frame={frame} />;
    }

    return (
        <OrderFrameBody
            key={frame.recordId}
            frame={frame}
            embedded={props.embedded ?? false}
            staging={props.staging ?? false}
            openOrder={openOrder}
        />
    );
}
